import { useEffect, useRef, useState } from 'react';
import {
  api, baixar, ErroApi, lerContexto, type ArquivoContador, type ArquivoFiscal, type CompetenciaLeitura,
  type DocumentoLeitura, type EmpresaLeitura, type LinhaLivro, type ResumoSped, type TipoArquivoContador,
} from '../api';
import { Carregando, Erro, Selo, Tabela, Vazio, cnpjFormatado } from '../componentes/ui';

const TIPO_ARQUIVO: Record<ArquivoFiscal['tipo'], string> = {
  EFD_ICMS_IPI: 'EFD ICMS/IPI', EFD_CONTRIBUICOES: 'EFD-Contribuições', INVENTARIO: 'Inventário',
  XML_DOCUMENTOS: 'XML do mês',
};
const TIPO_ARQUIVO_CONTADOR: Record<TipoArquivoContador, string> = {
  EXTRATO_FRONTEIRA: 'Extrato da fronteira', LISTA_CTE: 'Lista de CT-e', LIVRO_ESCRITURADO: 'Livro escriturado', OUTRO: 'Outro',
};
const RETORNO_ARQUIVO: Record<'IMPORTADO' | 'PARA_CONFERIR' | 'RECUSADO', { tipo: string; texto: string }> = {
  IMPORTADO: { tipo: 'ok', texto: 'Importado' },
  PARA_CONFERIR: { tipo: 'atencao', texto: 'Para conferir' },
  RECUSADO: { tipo: 'atencao', texto: 'Recusado' },
};
const LIMITE_ENVIO = 100 * 1024 * 1024;
const kb = (b: number) => `${(b / 1024).toFixed(0)} KB`;
const tamanho = (b: number) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : kb(b));

function listarArquivosContador(empresaId: number, competencia: string): Promise<ArquivoContador[]> {
  return api.leituraArquivosContador(empresaId, competencia).then((r) => r.arquivos).catch(() => []);
}

/**
 * Documentos: o contador (e o cliente) escolhe a empresa e o mês, vê as
 * notas e baixa o XML de uma ou o pacote do mês inteiro (zip com saídas,
 * entradas e eventos). Cada download fica registrado na auditoria.
 */
const moeda = (v: string | null) =>
  v === null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const mes = (c: string) => {
  const [a, m] = c.split('-');
  return `${['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'][Number(m) - 1]}/${a}`;
};
const SITUACAO: Record<string, string> = { autorizada: 'ok', cancelada: 'atencao', denegada: 'atencao' };

export function Documentos() {
  const [empresas, setEmpresas] = useState<EmpresaLeitura[] | null>(null);
  const [empresaId, setEmpresaId] = useState<number | null>(null);
  const [competencias, setCompetencias] = useState<CompetenciaLeitura[] | null>(null);
  const [competencia, setCompetencia] = useState<string | null>(null);
  const [documentos, setDocumentos] = useState<DocumentoLeitura[] | null>(null);
  const [arquivosFiscais, setArquivosFiscais] = useState<ArquivoFiscal[] | null>(null);
  const [arquivosContador, setArquivosContador] = useState<ArquivoContador[] | null>(null);
  const [erro, setErro] = useState('');
  const [baixando, setBaixando] = useState(false);

  useEffect(() => {
    api.leituraEmpresas()
      .then((r) => {
        setEmpresas(r.empresas);
        if (r.empresas.length === 1) setEmpresaId(r.empresas[0].id);
      })
      .catch((e) => setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar as empresas.'));
  }, []);

  useEffect(() => {
    setCompetencias(null);
    setCompetencia(null);
    setDocumentos(null);
    if (!empresaId) return;
    api.leituraCompetencias(empresaId)
      .then((r) => {
        setCompetencias(r.competencias);
        if (r.competencias[0]) setCompetencia(r.competencias[0].competencia);
      })
      .catch((e) => setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar os meses.'));
  }, [empresaId]);

  useEffect(() => {
    setDocumentos(null);
    setArquivosFiscais(null);
    setArquivosContador(null);
    if (!empresaId || !competencia) return;
    api.leituraDocumentos(empresaId, competencia)
      .then((r) => setDocumentos(r.documentos))
      .catch((e) => setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar as notas.'));
    api.leituraArquivosFiscais(empresaId, competencia)
      .then((r) => setArquivosFiscais(r.arquivos))
      .catch(() => setArquivosFiscais([]));
    void listarArquivosContador(empresaId, competencia).then(setArquivosContador);
  }, [empresaId, competencia]);

  const empresa = empresas?.find((e) => e.id === empresaId) ?? null;
  const podeEnviar = !['admin', 'softhouse'].includes(lerContexto()?.tipo ?? '');

  function recarregarArquivosFiscais() {
    if (empresaId && competencia) {
      api.leituraArquivosFiscais(empresaId, competencia).then((r) => setArquivosFiscais(r.arquivos)).catch(() => {});
    }
  }

  function recarregarArquivosContador() {
    if (empresaId && competencia) void listarArquivosContador(empresaId, competencia).then(setArquivosContador);
  }

  async function baixarArquivo(caminho: string, nome: string) {
    setErro('');
    setBaixando(true);
    try {
      await baixar(caminho, nome);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui baixar.');
    } finally {
      setBaixando(false);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Documentos</h1>
          <p>As notas das suas empresas, do jeito que a SEFAZ autorizou. Baixe uma ou o pacote do mês.</p>
        </div>
      </header>

      <Erro>{erro}</Erro>

      {!empresas ? <Carregando /> : empresas.length === 0 ? (
        <Vazio
          titulo="Nenhuma empresa ainda"
          descricao="Quando uma empresa do seu escritório começar a enviar notas, ela aparece aqui."
        />
      ) : (
        <>
          <div className="doc-filtros">
            <div className="doc-empresa">
              <label className="field-label" htmlFor="doc-empresa">Empresa</label>
              <select id="doc-empresa" className="input" value={empresaId ?? ''} onChange={(e) => setEmpresaId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">Escolha…</option>
                {empresas.map((e) => (
                  <option key={e.id} value={e.id}>{e.razaoSocial} · {cnpjFormatado(e.cnpj)}</option>
                ))}
              </select>
            </div>
          </div>

          {empresa && (!competencias ? <Carregando /> : competencias.length === 0 ? (
            <Vazio titulo="Sem notas ainda" descricao="Esta empresa ainda não enviou nenhuma nota." />
          ) : (
            <>
              <div className="doc-meses" role="tablist" aria-label="Mês">
                {competencias.map((c) => (
                  <button key={c.competencia} role="tab" aria-selected={c.competencia === competencia}
                    className={'doc-mes' + (c.competencia === competencia ? ' ativo' : '')}
                    onClick={() => setCompetencia(c.competencia)}>
                    <strong>{mes(c.competencia)}</strong>
                    <span>{c.qtdNfe + c.qtdNfce} nota{c.qtdNfe + c.qtdNfce === 1 ? '' : 's'}</span>
                    {c.divergencias > 0 && <span className="selo atencao">{c.divergencias} divergência{c.divergencias > 1 ? 's' : ''}</span>}
                  </button>
                ))}
              </div>

              {competencia && (
                <div className="doc-acoes">
                  <span>
                    {empresa.razaoSocial} · {mes(competencia)}
                    {documentos ? ` · ${documentos.length} documento${documentos.length === 1 ? '' : 's'}` : ''}
                  </span>
                  <button className="btn btn-primary btn-sm" disabled={baixando}
                    onClick={() => baixarArquivo(`/leitura/empresas/${empresa.id}/competencias/${competencia}/zip`, `${empresa.cnpj}-${competencia}.zip`)}>
                    Baixar o mês (zip)
                  </button>
                </div>
              )}

              {!documentos ? <Carregando /> : (
                <Tabela
                  itens={documentos}
                  chaveDe={(d) => d.id}
                  classeLinha={(d) => (d.situacao === 'cancelada' ? 'linha-inativa' : undefined)}
                  colunas={[
                    { rotulo: 'Nota', principal: true, celula: (d) => <strong>{d.tipo} {d.numero} · série {d.serie}</strong> },
                    { rotulo: 'Emissão', celula: (d) => new Date(d.emitidoEm).toLocaleDateString('pt-BR') },
                    { rotulo: 'Sentido', fraco: true, celula: (d) => (d.sentido === 'saida' ? 'Saída' : 'Entrada') },
                    { rotulo: 'Destinatário', fraco: true, celula: (d) => d.destinatario ?? '—' },
                    { rotulo: 'Valor', numero: true, celula: (d) => moeda(d.valorTotal) },
                    { rotulo: 'Situação', celula: (d) => <Selo tipo={SITUACAO[d.situacao] ?? 'neutro'} texto={d.situacao} /> },
                    {
                      rotulo: 'XML', acoes: true,
                      celula: (d) => (
                        <button className="link-botao" disabled={baixando}
                          onClick={() => baixarArquivo(`/leitura/empresas/${empresa.id}/documentos/${competencia}/${d.id}/xml`, `${d.chave44}.xml`)}>
                          Baixar XML
                        </button>
                      ),
                    },
                  ]}
                />
              )}

              {arquivosFiscais && (arquivosFiscais.length > 0 || podeEnviar) && (
                <div className="bloco">
                  <h2>Arquivos fiscais</h2>
                  {podeEnviar && competencia && (
                    <EnviarXmlDocumentos empresaId={empresa.id} competencia={competencia} aoEnviar={recarregarArquivosFiscais} />
                  )}
                  {arquivosFiscais.length > 0 && (
                  <Tabela
                    itens={arquivosFiscais}
                    chaveDe={(a) => a.id}
                    colunas={[
                      { rotulo: 'Arquivo', principal: true, celula: (a) => <strong>{TIPO_ARQUIVO[a.tipo]}</strong> },
                      { rotulo: 'Nome', fraco: true, celula: (a) => a.nomeArquivo },
                      { rotulo: 'Finalidade', fraco: true, celula: (a) => (a.finalidade === 'retificadora' ? 'Retificadora' : 'Original') },
                      { rotulo: 'Tamanho', fraco: true, celula: (a) => kb(a.tamanhoBytes) },
                      { rotulo: 'Recebido em', fraco: true, celula: (a) => new Date(a.recebidoEm).toLocaleString('pt-BR') },
                      {
                        rotulo: 'Ações', acoes: true,
                        celula: (a) => (
                          <button className="link-botao" disabled={baixando}
                            onClick={() => baixarArquivo(`/leitura/empresas/${empresa.id}/arquivos-fiscais/${a.id}`, a.nomeArquivo)}>
                            Baixar
                          </button>
                        ),
                      },
                    ]}
                  />
                  )}
                </div>
              )}
              {arquivosFiscais?.filter((a) => a.resumo).map((a) => (
                <ResumoSpedBloco key={a.id} arquivo={a} baixando={baixando}
                  aoBaixar={() => baixarArquivo(`/leitura/empresas/${empresa.id}/arquivos-fiscais/${a.id}`, a.nomeArquivo)} />
              ))}
              {competencia && (podeEnviar || (arquivosContador && arquivosContador.length > 0)) && (
                <ArquivosDoContador
                  empresaId={empresa.id}
                  competencia={competencia}
                  arquivos={arquivosContador}
                  podeEnviar={podeEnviar}
                  aoMudar={recarregarArquivosContador}
                  aoBaixar={baixarArquivo}
                />
              )}
            </>
          ))}
        </>
      )}
    </div>
  );
}

const ROTULOS_E110: [keyof NonNullable<ResumoSped['e110']>, string][] = [
  ['debitos', 'Débitos'], ['creditos', 'Créditos'], ['ajustesDebito', 'Ajustes de débito'], ['ajustesCredito', 'Ajustes de crédito'],
  ['estornosCredito', 'Estornos de crédito'], ['estornosDebito', 'Estornos de débito'], ['saldoCredorAnterior', 'Saldo credor anterior'],
  ['deducoes', 'Deduções'], ['saldoDevedor', 'Saldo devedor'], ['saldoCredor', 'Saldo credor'], ['aRecolher', 'A recolher'], ['saldoCredorTransportar', 'Saldo credor a transportar'],
];
const dataBR = (d?: string) => (d ? d.split('-').reverse().join('/') : '—');
const valorBR = (v?: string) => (v === undefined || v === '' ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

function ResumoSpedBloco({ arquivo, aoBaixar, baixando }: { arquivo: ArquivoFiscal; aoBaixar: () => void; baixando: boolean }) {
  const r: ResumoSped = arquivo.resumo ?? {};
  const linhasLivros = [
    ...(r.livros?.entradas ?? []).map((l: LinhaLivro, i) => ({ ...l, chave: `e${i}`, sentido: 'Entrada' })),
    ...(r.livros?.saidas ?? []).map((l: LinhaLivro, i) => ({ ...l, chave: `s${i}`, sentido: 'Saída' })),
  ];
  const e110 = r.e110 ? ROTULOS_E110.filter(([k]) => r.e110?.[k] !== undefined) : [];

  return (
    <div className="bloco">
      <h2>Resumo · {TIPO_ARQUIVO[arquivo.tipo]} · {arquivo.finalidade === 'retificadora' ? 'Retificadora' : 'Original'}</h2>
      {r.cabecalho && (
        <p className="nota">
          CNPJ {r.cabecalho.cnpj ? cnpjFormatado(r.cabecalho.cnpj) : '—'} · IE {r.cabecalho.ie ?? '—'} ·
          período {dataBR(r.cabecalho.periodoInicio)} a {dataBR(r.cabecalho.periodoFim)}
          {r.cabecalho.perfil ? ` · perfil ${r.cabecalho.perfil}` : ''}
        </p>
      )}
      {r.semExtratoFronteira && <p className="nota">Este resumo foi gerado sem o extrato da fronteira.</p>}

      {e110.length > 0 && (
        <>
          <h3>Apuração (E110)</h3>
          <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px' }}>
            {e110.map(([k, rotulo]) => (
              <div key={k} style={{ display: 'contents' }}>
                <dt className="nota">{rotulo}</dt>
                <dd style={{ margin: 0 }}><strong>{valorBR(r.e110?.[k])}</strong></dd>
              </div>
            ))}
          </dl>
        </>
      )}

      {linhasLivros.length > 0 && (
        <>
          <h3>Livros por CFOP</h3>
          <Tabela
            itens={linhasLivros}
            chaveDe={(l) => l.chave}
            colunas={[
              { rotulo: 'Sentido', principal: true, celula: (l) => l.sentido },
              { rotulo: 'CFOP', celula: (l) => l.cfop },
              { rotulo: 'Valor contábil', numero: true, celula: (l) => valorBR(l.valorContabil) },
              { rotulo: 'Base de cálculo', numero: true, celula: (l) => valorBR(l.baseCalculo) },
              { rotulo: 'ICMS', numero: true, celula: (l) => valorBR(l.icms) },
              { rotulo: 'Isentas', numero: true, celula: (l) => valorBR(l.isentas) },
              { rotulo: 'Outras', numero: true, celula: (l) => valorBR(l.outras) },
            ]}
          />
        </>
      )}

      {r.e111 && r.e111.length > 0 && (
        <>
          <h3>Ajustes (E111)</h3>
          <ul className="nota">
            {r.e111.map((a, i) => (
              <li key={`${a.codigo}-${i}`}>
                {a.codigo} · {valorBR(a.valor)}{a.notasE113?.length ? ` · ${a.notasE113.length} nota${a.notasE113.length > 1 ? 's' : ''} (E113)` : ''}
              </li>
            ))}
          </ul>
        </>
      )}

      {r.e116 && r.e116.length > 0 && (
        <>
          <h3>Guias a recolher (E116)</h3>
          <ul className="nota">
            {r.e116.map((g, i) => (
              <li key={`${g.codigo}-${i}`}>
                {g.codigoReceita ? `${g.codigoReceita} (código de receita) · ` : ''}{g.codigo} · {valorBR(g.valor)} · vencimento {dataBR(g.vencimento)}
              </li>
            ))}
          </ul>
        </>
      )}

      {(r.inventario || r.documentos) && (
        <p className="nota">
          {r.inventario && `Inventário: ${r.inventario.itens ?? 0} itens, valor total ${valorBR(r.inventario.valorTotal)}.`}
          {r.inventario && r.documentos && ' '}
          {r.documentos && `Documentos: C100 ${r.documentos.c100 ?? 0}, D100 ${r.documentos.d100 ?? 0}, NFC-e ${r.documentos.nfce ?? 0}.`}
        </p>
      )}

      <div className="doc-acoes">
        <button className="btn btn-sm" disabled={baixando} onClick={aoBaixar}>Baixar o .txt</button>
      </div>
    </div>
  );
}

function EnviarXmlDocumentos({ empresaId, competencia, aoEnviar }: {
  empresaId: number; competencia: string; aoEnviar: () => void;
}) {
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState('');
  const [erro, setErro] = useState('');
  const entrada = useRef<HTMLInputElement>(null);

  async function enviar(arquivo: File) {
    setErro('');
    setAviso('');
    if (arquivo.size > LIMITE_ENVIO) {
      setErro('Esse zip passa de 100 MB.');
      return;
    }
    setEnviando(true);
    try {
      const r = await api.enviarXmlDocumentos(empresaId, competencia, arquivo);
      setAviso(r.duplicado ? 'Esse zip já estava enviado para esta empresa neste mês.' : 'Zip enviado.');
      aoEnviar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui enviar o zip.');
    } finally {
      setEnviando(false);
      if (entrada.current) entrada.current.value = '';
    }
  }

  return (
    <div className="doc-acoes">
      <button className="btn btn-sm" disabled={enviando} onClick={() => entrada.current?.click()}>
        {enviando ? <span className="spinner" /> : 'Enviar XML do mês (zip)'}
      </button>
      <input ref={entrada} type="file" accept=".zip,application/zip" hidden
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void enviar(f); }} />
      <Erro>{erro}</Erro>
      {aviso && <span className="nota">{aviso}</span>}
    </div>
  );
}

function ArquivosDoContador({
  empresaId, competencia, arquivos, podeEnviar, aoMudar, aoBaixar,
}: {
  empresaId: number;
  competencia: string;
  arquivos: ArquivoContador[] | null;
  podeEnviar: boolean;
  aoMudar: () => void;
  aoBaixar: (caminho: string, nome: string) => Promise<void>;
}) {
  const [tipo, setTipo] = useState<TipoArquivoContador>('EXTRATO_FRONTEIRA');
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState('');
  const [erro, setErro] = useState('');
  const entrada = useRef<HTMLInputElement>(null);

  async function enviar(arquivo: File) {
    setErro('');
    setAviso('');
    if (arquivo.size > LIMITE_ENVIO) {
      setErro('Esse arquivo passa de 100 MB. Divida em partes menores.');
      return;
    }
    setEnviando(true);
    try {
      const r = await api.enviarArquivoContador(empresaId, competencia, tipo, arquivo);
      setAviso(r.duplicado ? 'Esse arquivo já estava enviado para esta empresa neste mês.' : 'Arquivo enviado.');
      aoMudar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui enviar o arquivo.');
    } finally {
      setEnviando(false);
      if (entrada.current) entrada.current.value = '';
    }
  }

  return (
    <div className="bloco">
      <h2>Arquivos do contador</h2>
      <p className="nota">Extratos, listas e livros que vão para esta empresa neste mês. Até 100 MB por arquivo.</p>

      {podeEnviar && (
        <div className="doc-acoes">
          <select className="input" value={tipo} disabled={enviando} aria-label="Tipo do arquivo"
            onChange={(e) => setTipo(e.target.value as TipoArquivoContador)}>
            {(Object.keys(TIPO_ARQUIVO_CONTADOR) as TipoArquivoContador[]).map((t) => (
              <option key={t} value={t}>{TIPO_ARQUIVO_CONTADOR[t]}</option>
            ))}
          </select>
          <button className="btn btn-primary btn-sm" disabled={enviando} onClick={() => entrada.current?.click()}>
            {enviando ? <span className="spinner" /> : 'Enviar arquivo'}
          </button>
          <input ref={entrada} type="file" hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void enviar(f); }} />
        </div>
      )}

      <Erro>{erro}</Erro>
      {aviso && <p className="nota">{aviso}</p>}

      {!arquivos ? <Carregando /> : arquivos.length === 0 ? (
        <p className="nota">Nenhum arquivo enviado neste mês.</p>
      ) : (
        <Tabela
          itens={arquivos}
          chaveDe={(a) => a.id}
          colunas={[
            { rotulo: 'Arquivo', principal: true, celula: (a) => <strong>{TIPO_ARQUIVO_CONTADOR[a.tipo]}</strong> },
            { rotulo: 'Nome', fraco: true, celula: (a) => a.nomeArquivo },
            { rotulo: 'Tamanho', fraco: true, celula: (a) => tamanho(a.tamanhoBytes) },
            { rotulo: 'Enviado', fraco: true, celula: (a) => `${new Date(a.enviadoEm).toLocaleString('pt-BR')} · ${a.enviadoPor}` },
            {
              rotulo: 'Retorno',
              celula: (a) => a.retorno ? (
                <>
                  <Selo tipo={RETORNO_ARQUIVO[a.retorno.situacao].tipo} texto={RETORNO_ARQUIVO[a.retorno.situacao].texto} />
                  {a.retorno.mensagem && <div className="nota">{a.retorno.mensagem}</div>}
                </>
              ) : <Selo tipo="neutro" texto="Aguardando" />,
            },
            {
              rotulo: 'Ações', acoes: true,
              celula: (a) => (
                <button className="link-botao" disabled={enviando}
                  onClick={() => aoBaixar(`/leitura/empresas/${empresaId}/arquivos-contador/${a.id}`, a.nomeArquivo)}>
                  Baixar
                </button>
              ),
            },
          ]}
        />
      )}
    </div>
  );
}
